"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";

function cloudTexture(): THREE.CanvasTexture {
  const textureCanvas = document.createElement("canvas");
  textureCanvas.width = 256;
  textureCanvas.height = 256;
  const textureContext = textureCanvas.getContext("2d");
  if (!textureContext) {
    return new THREE.CanvasTexture(textureCanvas);
  }

  const gradient = textureContext.createRadialGradient(128, 128, 8, 128, 128, 128);
  gradient.addColorStop(0, "rgba(190, 196, 210, 0.38)");
  gradient.addColorStop(0.45, "rgba(126, 136, 158, 0.16)");
  gradient.addColorStop(1, "rgba(50, 55, 75, 0)");
  textureContext.fillStyle = gradient;
  textureContext.fillRect(0, 0, 256, 256);

  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export function StormBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x11111f, 0.004);
    const camera = new THREE.PerspectiveCamera(60, 1, 1, 1000);
    camera.position.z = 1;
    camera.rotation.set(1.16, -0.12, 0.27);

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
      powerPreference: "low-power",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setClearColor(scene.fog.color, 0);

    scene.add(new THREE.AmbientLight(0x555555));
    const stormLight = new THREE.PointLight(0x6d9ee8, 0, 500, 1.7);
    stormLight.position.set(200, 300, 100);
    scene.add(stormLight);

    const bounds = canvas.getBoundingClientRect();
    const rainCount = Math.max(900, Math.min(2600, Math.round((bounds.width * bounds.height) / 350)));
    const rainPositions = new Float32Array(rainCount * 3);
    for (let index = 0; index < rainCount; index += 1) {
      const offset = index * 3;
      rainPositions[offset] = Math.random() * 400 - 200;
      rainPositions[offset + 1] = Math.random() * 500 - 250;
      rainPositions[offset + 2] = Math.random() * 400 - 200;
    }
    const rainGeometry = new THREE.BufferGeometry();
    rainGeometry.setAttribute("position", new THREE.BufferAttribute(rainPositions, 3));
    const rainMaterial = new THREE.PointsMaterial({
      color: 0xadb5c6,
      size: 0.4,
      transparent: true,
      opacity: 0.52,
      depthWrite: false,
    });
    const rain = new THREE.Points(rainGeometry, rainMaterial);
    scene.add(rain);

    const fogTexture = cloudTexture();
    const cloudGeometry = new THREE.PlaneGeometry(500, 500);
    const cloudMaterial = new THREE.MeshLambertMaterial({
      map: fogTexture,
      transparent: true,
      depthWrite: false,
    });
    const clouds = Array.from({ length: 14 }, () => {
      const cloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
      cloud.position.set(
        Math.random() * 800 - 400,
        500,
        Math.random() * 500 - 450,
      );
      cloud.rotation.set(1.16, -0.12, Math.random() * Math.PI * 2);
      scene.add(cloud);
      return cloud;
    });

    const resize = () => {
      const nextBounds = canvas.getBoundingClientRect();
      if (!nextBounds.width || !nextBounds.height) {
        return;
      }
      camera.aspect = nextBounds.width / nextBounds.height;
      camera.updateProjectionMatrix();
      renderer.setSize(nextBounds.width, nextBounds.height, false);
    };

    let animationFrame = 0;
    const animate = () => {
      for (const cloud of clouds) {
        cloud.rotation.z -= 0.0015;
      }
      rain.position.z = rain.position.z < -200 ? 0 : rain.position.z - 0.15;

      if (Math.random() > 0.992 || stormLight.intensity > 0.35) {
        if (stormLight.intensity < 0.35) {
          stormLight.position.set(Math.random() * 400 - 200, 300 + Math.random() * 200, 100);
          stormLight.intensity = 2.4 + Math.random() * 4;
        } else {
          stormLight.intensity *= 0.88;
        }
      }

      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      rainGeometry.dispose();
      rainMaterial.dispose();
      cloudGeometry.dispose();
      cloudMaterial.dispose();
      fogTexture.dispose();
      renderer.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 size-full" />;
}